import { orderKeyAfterLast, orderKeyBetween } from "@vmeste/shared";
import type {
  Attachment,
  Event,
  EventParticipant,
  MessageReaction,
  List,
  ListItem,
  Message,
  Place,
  PlaceReview,
  SpaceMember,
  SyncDelta,
  Space,
  Tag,
  TagLink,
  Topic,
  Wish,
} from "@vmeste/shared";

/**
 * The local mirror of one space, and the pure function that advances it.
 *
 * Kept free of Dexie, React and fetch so the part most likely to lose somebody's
 * data — merging a delta into what is already on the device — can be tested
 * directly rather than through a browser.
 */

export interface SpaceState {
  spaceId: string;
  /** Highest revision this device has applied. The cursor for the next pull. */
  revision: number;
  space: Space | null;
  members: SpaceMember[];
  lists: Record<string, List>;
  items: Record<string, ListItem>;
  wishes: Record<string, Wish>;
  places: Record<string, Place>;
  /** Keyed `placeId:userId` — an opinion is addressed by whose it is. */
  placeReviews: Record<string, PlaceReview>;
  topics: Record<string, Topic>;
  messages: Record<string, Message>;
  events: Record<string, Event>;
  /** Keyed `eventId:userId`, matching the server's primary key. */
  eventParticipants: Record<string, EventParticipant>;
  /** Keyed by `messageId:userId` — a reaction has no id of its own. */
  reactions: Record<string, MessageReaction>;
  tags: Record<string, Tag>;
  /** Keyed `tagId:targetType:targetId`, matching the server's primary key. */
  tagLinks: Record<string, TagLink>;
  attachments: Record<string, Attachment>;
}

/** The key a tag link is stored under. */
export const tagLinkKey = (link: {
  tagId: string;
  targetType: string;
  targetId: string;
}): string => `${link.tagId}:${link.targetType}:${link.targetId}`;

/** The key a reaction is stored under. Its identity is the pair it names. */
export const reactionKey = (row: { messageId: string; userId: string }): string =>
  `${row.messageId}:${row.userId}`;

/** The key a participation is stored under. Its identity is the pair it names. */
export const eventParticipantKey = (row: {
  eventId: string;
  userId: string;
}): string => `${row.eventId}:${row.userId}`;

export function emptySpaceState(spaceId: string): SpaceState {
  return {
    spaceId,
    revision: 0,
    space: null,
    members: [],
    lists: {},
    items: {},
    wishes: {},
    places: {},
    placeReviews: {},
    topics: {},
    messages: {},
    events: {},
    eventParticipants: {},
    reactions: {},
    tags: {},
    tagLinks: {},
    attachments: {},
  };
}

function index<T extends { id: string }>(rows: readonly T[]): Record<string, T> {
  const out: Record<string, T> = {};
  for (const row of rows) out[row.id] = row;
  return out;
}

/**
 * Merge rows in, dropping anything the server marked deleted.
 *
 * A tombstone is not kept around: the row is gone, and holding an entry with
 * `deletedAt` set would make every reader remember to filter it out.
 */
function merge<T extends { id: string; revision: number; deletedAt: string | null }>(
  current: Record<string, T>,
  incoming: readonly T[],
): Record<string, T> {
  if (incoming.length === 0) return current;

  const next = { ...current };

  for (const row of incoming) {
    if (row.deletedAt !== null) {
      delete next[row.id];
      continue;
    }

    const existing = next[row.id];
    // A delta can be replayed — a reconnect races the WebSocket nudge — so an
    // older revision for a row we already have must not overwrite it.
    if (existing && existing.revision > row.revision) continue;

    next[row.id] = row;
  }

  return next;
}

/**
 * The same two functions again, for rows whose identity is not an id.
 *
 * A tag link is the triple it links; a participation is the pair it names.
 * Neither has an id of its own, on purpose and on both sides of the wire — so
 * the key is computed rather than read off the row, and the caller says how.
 */
type Keyed = { revision: number; deletedAt: string | null };

function indexBy<T extends Keyed>(
  rows: readonly T[],
  keyOf: (row: T) => string,
): Record<string, T> {
  const out: Record<string, T> = {};
  for (const row of rows) out[keyOf(row)] = row;
  return out;
}

function mergeBy<T extends Keyed>(
  current: Record<string, T>,
  incoming: readonly T[],
  keyOf: (row: T) => string,
): Record<string, T> {
  if (incoming.length === 0) return current;

  const next = { ...current };

  for (const row of incoming) {
    const key = keyOf(row);

    if (row.deletedAt !== null) {
      delete next[key];
      continue;
    }

    const existing = next[key];
    // A delta can be replayed — a reconnect races the WebSocket nudge — so an
    // older revision for a row we already have must not overwrite it.
    if (existing && existing.revision > row.revision) continue;

    next[key] = row;
  }

  return next;
}

/**
 * Advance local state by one delta.
 *
 * `reset` means the server could not answer incrementally and sent a full
 * snapshot, so local rows are replaced rather than merged: whatever is missing
 * from the snapshot no longer exists (SPEC §7).
 */
export function applyDelta(state: SpaceState, delta: SyncDelta): SpaceState {
  if (delta.reset) {
    return {
      spaceId: state.spaceId,
      revision: delta.revision,
      space: delta.space,
      members: delta.members,
      lists: index(delta.lists.filter((row) => row.deletedAt === null)),
      items: index(delta.items.filter((row) => row.deletedAt === null)),
      wishes: index(delta.wishes.filter((row) => row.deletedAt === null)),
      places: index(delta.places.filter((row) => row.deletedAt === null)),
      placeReviews: indexBy(
        delta.placeReviews.filter((row) => row.deletedAt === null),
        placeReviewKey,
      ),
      topics: index(delta.topics.filter((row) => row.deletedAt === null)),
      messages: index(delta.messages.filter((row) => row.deletedAt === null)),
      events: index(delta.events.filter((row) => row.deletedAt === null)),
      eventParticipants: indexBy(
        delta.eventParticipants.filter((row) => row.deletedAt === null),
        eventParticipantKey,
      ),
      reactions: indexBy(
        delta.reactions.filter((row) => row.deletedAt === null),
        reactionKey,
      ),
      tags: index(delta.tags.filter((row) => row.deletedAt === null)),
      tagLinks: indexBy(
        delta.tagLinks.filter((row) => row.deletedAt === null),
        tagLinkKey,
      ),
      attachments: index(delta.attachments.filter((row) => row.deletedAt === null)),
    };
  }

  return {
    spaceId: state.spaceId,
    // Never move the cursor backwards: an out-of-order delta would otherwise
    // make the next pull re-request rows already applied, or worse, skip some.
    revision: Math.max(state.revision, delta.revision),
    space: delta.space,
    members: delta.members.length > 0 ? delta.members : state.members,
    lists: merge(state.lists, delta.lists),
    items: merge(state.items, delta.items),
    wishes: merge(state.wishes, delta.wishes),
    places: merge(state.places, delta.places),
    placeReviews: mergeBy(state.placeReviews, delta.placeReviews, placeReviewKey),
    topics: merge(state.topics, delta.topics),
    messages: merge(state.messages, delta.messages),
    events: merge(state.events, delta.events),
    eventParticipants: mergeBy(
      state.eventParticipants,
      delta.eventParticipants,
      eventParticipantKey,
    ),
    reactions: mergeBy(state.reactions, delta.reactions, reactionKey),
    tags: merge(state.tags, delta.tags),
    tagLinks: mergeBy(state.tagLinks, delta.tagLinks, tagLinkKey),
    attachments: merge(state.attachments, delta.attachments),
  };
}

/* -------------------------------------------------------------- selectors */

/** Lists in display order, archived ones excluded unless asked for. */
export function selectLists(
  state: SpaceState,
  options: { kind?: List["kind"]; includeArchived?: boolean } = {},
): List[] {
  return Object.values(state.lists)
    .filter((list) => options.kind === undefined || list.kind === options.kind)
    .filter((list) => options.includeArchived === true || list.archivedAt === null)
    .sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0));
}

/**
 * Top-level items of a list, in display order, with done ones sunk to the
 * bottom — a ticked-off item should not keep its place in the middle of a
 * shopping list you are reading in a supermarket.
 */
export function selectItems(state: SpaceState, listId: string): ListItem[] {
  return Object.values(state.items)
    .filter((item) => item.listId === listId && item.parentId === null)
    .sort(byDoneThenPosition);
}

export function selectChildren(state: SpaceState, parentId: string): ListItem[] {
  return Object.values(state.items)
    .filter((item) => item.parentId === parentId)
    .sort(byDoneThenPosition);
}

function byDoneThenPosition(a: ListItem, b: ListItem): number {
  if (a.done !== b.done) return a.done ? 1 : -1;
  return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
}

export function selectMember(
  state: SpaceState,
  userId: string | null,
): SpaceMember | null {
  if (userId === null) return null;
  return state.members.find((member) => member.userId === userId) ?? null;
}

/** Positions already taken in a list, which is what a new item is placed after. */
export function positionsIn(state: SpaceState, listId: string): string[] {
  return Object.values(state.items)
    .filter((item) => item.listId === listId)
    .map((item) => item.position);
}

/**
 * Wishes in display order, with the ones already dealt with at the bottom.
 *
 * A bought or gifted wish stays visible rather than disappearing: half the point
 * of the list is remembering what was given and when.
 */
export function selectWishes(
  state: SpaceState,
  options: { status?: Wish["status"] } = {},
): Wish[] {
  return Object.values(state.wishes)
    .filter((wish) => options.status === undefined || wish.status === options.status)
    .sort((a, b) => {
      const settled = (wish: Wish) => (wish.status === "idea" ? 0 : 1);
      if (settled(a) !== settled(b)) return settled(a) - settled(b);
      return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
    });
}

/** Positions already taken, which is what a new wish is placed after. */
export function wishPositions(state: SpaceState): string[] {
  return Object.values(state.wishes).map((wish) => wish.position);
}

/* ---------------------------------------------------------------- places */

/** A review is addressed by the pair it belongs to, never by its own id. */
export function placeReviewKey(review: Pick<PlaceReview, "placeId" | "userId">): string {
  return `${review.placeId}:${review.userId}`;
}

/**
 * Places, newest first.
 *
 * Not sorted by rating or by distance, and neither is an oversight. A rating
 * sort would bury the half of the map that is «ещё не были», which is the half
 * somebody opens this screen to act on; distance changes while you read it.
 * The map itself is the spatial view — this order is for the list beside it.
 */
export function selectPlaces(state: SpaceState): Place[] {
  return Object.values(state.places).sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
  );
}

/** Every opinion of one place, in no particular order — there are two at most. */
export function reviewsOf(state: SpaceState, placeId: string): PlaceReview[] {
  return Object.values(state.placeReviews).filter((review) => review.placeId === placeId);
}

export function reviewBy(
  state: SpaceState,
  placeId: string,
  userId: string,
): PlaceReview | null {
  return state.placeReviews[placeReviewKey({ placeId, userId })] ?? null;
}

/**
 * What the pair thought of a place, as one number, or null if nobody has said.
 *
 * The mean of however many opinions exist, which for two people is either one
 * person's score or the middle of two. Rounded nowhere: a pin drawn as «4.5»
 * is telling the truth about a disagreement, and rounding it to 5 would not.
 */
export function ratingOf(state: SpaceState, placeId: string): number | null {
  const scores = reviewsOf(state, placeId).map((review) => review.rating);
  if (scores.length === 0) return null;

  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}

/**
 * The position key an item needs to land at `toIndex` among the open items of
 * its list, or null when the move is a no-op.
 *
 * Kept here, apart from React and drag-and-drop, because it is the part that can
 * silently corrupt an ordering: only the *open* items are reorderable — done
 * ones sink to their own group — so computing a key from the full list would
 * derive it from neighbours the person cannot see.
 */
export function positionForMove(
  state: SpaceState,
  listId: string,
  itemId: string,
  toIndex: number,
): string | null {
  const siblings = selectItems(state, listId).filter((item) => !item.done);
  const from = siblings.findIndex((item) => item.id === itemId);
  if (from === -1) return null;

  const target = Math.max(0, Math.min(toIndex, siblings.length - 1));

  // Already where it was asked to go. Comparing indices rather than neighbouring
  // keys: for a move into the middle the neighbours are the edges of the gap,
  // never the moved row itself, so a key comparison never fires and every drop
  // would burn a revision and push a pointless delta at the other device.
  if (from === target) return null;

  const without = siblings.filter((item) => item.id !== itemId);

  const before = target > 0 ? without[target - 1]!.position : null;
  const after = target < without.length ? without[target]!.position : null;

  return orderKeyBetween(before, after);
}

/* ------------------------------------------------------------------ chat */

/** The space's single running conversation, if it has been created yet. */
export function selectGeneralTopic(state: SpaceState): Topic | null {
  return Object.values(state.topics).find((topic) => topic.kind === "general") ?? null;
}

/**
 * Threads: pinned ones first in the order somebody arranged them, then the rest
 * most recently active first — the order a person scans them in.
 *
 * Pinning exists because threads breed on their own: every wish, item and event
 * that gets discussed makes one, so "most recent" alone buries the few that
 * matter for weeks under the ones that mattered for an afternoon.
 */
export function selectThreads(
  state: SpaceState,
  options: { status?: Topic["status"] } = {},
): Topic[] {
  return Object.values(state.topics)
    .filter((topic) => topic.kind === "thread")
    .filter((topic) => options.status === undefined || topic.status === options.status)
    .sort((a, b) => {
      const pinned = (topic: Topic) => (topic.pinnedAt === null ? 1 : 0);
      if (pinned(a) !== pinned(b)) return pinned(a) - pinned(b);
      if (pinned(a) === 0) {
        // A pinned row with no key yet is one the server has still to place;
        // it waits at the bottom of the group rather than jumping to the top.
        const key = (topic: Topic) => topic.pinnedOrder;
        if (key(a) === null || key(b) === null) {
          return (key(a) === null ? 1 : 0) - (key(b) === null ? 1 : 0);
        }
        return key(a)! < key(b)! ? -1 : key(a)! > key(b)! ? 1 : 0;
      }
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
}

/** The pinned threads alone, already in their arranged order. */
export function selectPinnedThreads(state: SpaceState): Topic[] {
  return selectThreads(state).filter((topic) => topic.pinnedAt !== null);
}

/** Where a topic being pinned goes: last among the pins, so nothing shifts. */
export function nextPinnedOrder(state: SpaceState): string {
  const keys = selectPinnedThreads(state)
    .map((topic) => topic.pinnedOrder)
    .filter((key): key is string => key !== null);

  return orderKeyAfterLast(keys);
}

/**
 * The key a pinned topic needs to land at `toIndex` among the pins, or null when
 * the move is a no-op.
 *
 * The twin of `positionForMove`, and here for the same reason: only the pinned
 * ones are reorderable, so a key derived from the whole list would be derived
 * from neighbours the person cannot see in that group.
 */
export function pinnedOrderForMove(
  state: SpaceState,
  topicId: string,
  toIndex: number,
): string | null {
  const pins = selectPinnedThreads(state);
  const from = pins.findIndex((topic) => topic.id === topicId);
  if (from === -1) return null;

  const target = Math.max(0, Math.min(toIndex, pins.length - 1));
  if (from === target) return null;

  const without = pins.filter((topic) => topic.id !== topicId);

  // The nearest neighbour that actually has a key, rather than the adjacent row:
  // a pin the server has not placed yet has none, and reading `null` off it
  // would be read as "there is nothing on that side" and fling the row to an end.
  let before: string | null = null;
  for (let i = target - 1; i >= 0 && before === null; i--) {
    before = without[i]!.pinnedOrder;
  }
  let after: string | null = null;
  for (let i = target; i < without.length && after === null; i++) {
    after = without[i]!.pinnedOrder;
  }

  return orderKeyBetween(before, after);
}

/**
 * The discussion about one thing, or nothing yet.
 *
 * At most one per target on purpose: two comment threads on the same wish is
 * two conversations nobody knows to look in. Nothing enforces that in the
 * database — a unique index would fight the tombstone, since a deleted
 * discussion should not block a new one — so the newest live one wins here and
 * the screen never offers to make a second.
 */
export function selectDiscussion(
  state: SpaceState,
  targetType: string,
  targetId: string,
): Topic | null {
  return (
    Object.values(state.topics)
      .filter((topic) => topic.targetType === targetType && topic.targetId === targetId)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0] ?? null
  );
}

/** Messages of one topic, oldest first, which is reading order. */
export function selectMessages(state: SpaceState, topicId: string): Message[] {
  return Object.values(state.messages)
    .filter((message) => message.topicId === topicId)
    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));
}

/** Is `message` later in the conversation than the row the cursor points at? */
function after(message: Message, cursor: Message): boolean {
  if (message.createdAt !== cursor.createdAt) return message.createdAt > cursor.createdAt;
  // Two messages in the same millisecond. Ids are uuid v7, so they sort the way
  // the rows were made.
  return message.id > cursor.id;
}

/**
 * How many messages arrived after the one this person last read.
 *
 * Their own messages never count as unread — you do not owe yourself a reply.
 *
 * **Compared by time, not by position in this topic**, and that is the fix
 * rather than a refinement. The cursor is one column on the membership
 * (`space_member.last_read_message_id`, SPEC §4.5) — one per space, not one per
 * topic — so looking for it *inside* a topic's own messages found nothing
 * whenever it was last set anywhere else, and every thread but one reported its
 * entire history as unread. With more than a single conversation that is every
 * thread, every time.
 *
 * A cursor naming a message this device has never seen — a fresh install, a row
 * still on its way — reads as "nothing read yet", which errs towards saying
 * there is something to look at rather than towards silence.
 */
export function countUnread(
  state: SpaceState,
  topicId: string,
  self: string,
  lastReadMessageId: string | null,
): number {
  const messages = selectMessages(state, topicId);
  if (messages.length === 0) return 0;

  const cursor = lastReadMessageId ? state.messages[lastReadMessageId] : undefined;

  return messages.filter(
    (message) => message.authorId !== self && (!cursor || after(message, cursor)),
  ).length;
}

/** The same question for the whole space: everything new, wherever it is. */
export function countUnreadInSpace(
  state: SpaceState,
  self: string,
  lastReadMessageId: string | null,
): number {
  const cursor = lastReadMessageId ? state.messages[lastReadMessageId] : undefined;

  return Object.values(state.messages).filter(
    (message) => message.authorId !== self && (!cursor || after(message, cursor)),
  ).length;
}

/** The newest message anywhere in the space — the row a read cursor moves to. */
export function newestMessage(state: SpaceState): Message | null {
  let newest: Message | null = null;
  for (const message of Object.values(state.messages)) {
    if (!newest || after(message, newest)) newest = message;
  }
  return newest;
}

/* -------------------------------------------------------------- calendar */

/**
 * Events overlapping a window, soonest first.
 *
 * A repeating event is always included: expanding its rule is the caller's job,
 * and filtering on `startsAt` would hide a weekly event whose first occurrence
 * predates the window.
 */
export function selectEvents(
  state: SpaceState,
  window: { from?: string; to?: string } = {},
): Event[] {
  return Object.values(state.events)
    .filter((event) => {
      if (event.rrule !== null) return true;
      const ends = event.endsAt ?? event.startsAt;
      if (window.from !== undefined && ends < window.from) return false;
      if (window.to !== undefined && event.startsAt > window.to) return false;
      return true;
    })
    .sort((a, b) => (a.startsAt < b.startsAt ? -1 : a.startsAt > b.startsAt ? 1 : 0));
}

/**
 * The answers given about one event.
 *
 * Only the rows that exist — a member who never answered has none, and turning
 * that absence into "не ответил" needs the member list, which is the caller's
 * to pair up.
 */
export function selectEventParticipants(
  state: SpaceState,
  eventId: string,
): EventParticipant[] {
  return Object.values(state.eventParticipants).filter(
    (row) => row.eventId === eventId,
  );
}

/** One person's answer about one event, if they have given one. */
export function selectEventParticipant(
  state: SpaceState,
  eventId: string,
  userId: string | null,
): EventParticipant | null {
  if (userId === null) return null;
  return state.eventParticipants[eventParticipantKey({ eventId, userId })] ?? null;
}

/* -------------------------------------------------------------- reactions */

/** Every reaction standing on one message. */
export function selectReactions(state: SpaceState, messageId: string): MessageReaction[] {
  return Object.values(state.reactions).filter((row) => row.messageId === messageId);
}

/** What this person has put on that message, if anything. */
export function selectReaction(
  state: SpaceState,
  messageId: string,
  userId: string | null,
): MessageReaction | null {
  if (userId === null) return null;
  return state.reactions[reactionKey({ messageId, userId })] ?? null;
}

/* ------------------------------------------------------------------- tags */

export function selectTags(state: SpaceState): Tag[] {
  return Object.values(state.tags);
}

/** The tags on one thing. */
export function selectTagsFor(
  state: SpaceState,
  targetType: string,
  targetId: string,
): Tag[] {
  return Object.values(state.tagLinks)
    .filter((link) => link.targetType === targetType && link.targetId === targetId)
    .map((link) => state.tags[link.tagId])
    .filter((tag): tag is Tag => tag !== undefined);
}

/* ------------------------------------------------------------ attachments */

export function selectAttachments(
  state: SpaceState,
  targetType: string,
  targetId: string,
): Attachment[] {
  return Object.values(state.attachments)
    .filter(
      (attachment) =>
        attachment.targetType === targetType && attachment.targetId === targetId,
    )
    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
}
